/**
 * useReelVoiceAssignment Hook
 *
 * Manages voice assignments for reels.
 * Supports default voice per reel and per-scene overrides.
 *
 * React 19 patterns:
 * - useCallback for event handlers
 * - useMemo for derived values
 */

import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { IReelVoiceAssignment, ISceneVoiceOverride, IVoiceAssignmentResult } from '@/@types/voices';

// ============================================
// HOOK
// ============================================

export function useReelVoiceAssignment(options: {
  reelId: Id<'reels'>;
  onSetDefaultSuccess?: (voiceId: Id<'clonedVoices'>) => void;
  onSetDefaultError?: (error: string) => void;
  onSetOverrideSuccess?: (sceneId: Id<'reelScenes'>, voiceId: Id<'clonedVoices'>) => void;
  onSetOverrideError?: (error: string) => void;
  onRemoveOverrideSuccess?: (sceneId: Id<'reelScenes'>) => void;
  onRemoveOverrideError?: (error: string) => void;
}) {
  const {
    reelId,
    onSetDefaultSuccess,
    onSetDefaultError,
    onSetOverrideSuccess,
    onSetOverrideError,
    onRemoveOverrideSuccess,
    onRemoveOverrideError,
  } = options;

  // ============================================
  // LOCAL STATE
  // ============================================
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [isSettingOverride, setIsSettingOverride] = useState(false);
  const [isRemovingOverride, setIsRemovingOverride] = useState(false);
  const [isClearingDefault, setIsClearingDefault] = useState(false);

  // ============================================
  // CONVEX QUERIES
  // ============================================

  // Fetch voice assignment for this reel
  const assignment = useQuery(api.reelVoiceAssignments.getReelVoiceAssignment, { reelId });

  // ============================================
  // CONVEX MUTATIONS
  // ============================================

  const setDefaultVoiceMutation = useMutation(api.reelVoiceAssignments.setDefaultVoice);
  const clearDefaultVoiceMutation = useMutation(api.reelVoiceAssignments.clearDefaultVoice);
  const setSceneOverrideMutation = useMutation(api.reelVoiceAssignments.setSceneVoiceOverride);
  const removeSceneOverrideMutation = useMutation(
    api.reelVoiceAssignments.removeSceneVoiceOverride
  );

  // ============================================
  // COMPUTED VALUES
  // ============================================

  const isLoading = useMemo(() => assignment === undefined, [assignment]);

  const defaultVoiceId = useMemo(() => assignment?.defaultVoiceId ?? null, [assignment]);

  const sceneOverrides = useMemo(
    () => (assignment?.sceneOverrides ?? []) as ISceneVoiceOverride[],
    [assignment]
  );

  const sceneOverridesMap = useMemo(() => {
    const map = new Map<string, Id<'clonedVoices'>>();
    for (const override of sceneOverrides) {
      map.set(override.sceneId, override.voiceId);
    }
    return map;
  }, [sceneOverrides]);

  const hasDefaultVoice = useMemo(() => defaultVoiceId !== null, [defaultVoiceId]);

  const hasAnyOverrides = useMemo(() => sceneOverrides.length > 0, [sceneOverrides]);

  const isProcessing = useMemo(
    () => isSettingDefault || isSettingOverride || isRemovingOverride || isClearingDefault,
    [isSettingDefault, isSettingOverride, isRemovingOverride, isClearingDefault]
  );

  // ============================================
  // EVENT HANDLERS
  // ============================================

  /**
   * Set the default voice for the reel
   */
  const handleSetDefaultVoice = useCallback(
    async (voiceId: Id<'clonedVoices'>): Promise<IVoiceAssignmentResult> => {
      setIsSettingDefault(true);

      try {
        const result = await setDefaultVoiceMutation({ reelId, voiceId });

        if (!result.success) {
          toast.error('Failed to set default voice', {
            description: result.error,
          });
          onSetDefaultError?.(result.error || 'Failed to set default voice');
          return result;
        }

        toast.success('Default voice set');
        onSetDefaultSuccess?.(voiceId);
        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        toast.error('Failed to set default voice', {
          description: errorMessage,
        });
        onSetDefaultError?.(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsSettingDefault(false);
      }
    },
    [reelId, setDefaultVoiceMutation, onSetDefaultSuccess, onSetDefaultError]
  );

  /**
   * Clear the default voice for the reel
   */
  const handleClearDefaultVoice = useCallback(async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    setIsClearingDefault(true);

    try {
      const result = await clearDefaultVoiceMutation({ reelId });

      if (!result.success) {
        toast.error('Failed to clear default voice', {
          description: result.error,
        });
        return result;
      }

      toast.success('Default voice cleared');
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Failed to clear default voice', {
        description: errorMessage,
      });
      return { success: false, error: errorMessage };
    } finally {
      setIsClearingDefault(false);
    }
  }, [reelId, clearDefaultVoiceMutation]);

  /**
   * Set a voice override for a specific scene
   */
  const handleSetSceneOverride = useCallback(
    async (
      sceneId: Id<'reelScenes'>,
      voiceId: Id<'clonedVoices'>
    ): Promise<{ success: boolean; error?: string }> => {
      setIsSettingOverride(true);

      try {
        const result = await setSceneOverrideMutation({ reelId, sceneId, voiceId });

        if (!result.success) {
          toast.error('Failed to set scene voice', {
            description: result.error,
          });
          onSetOverrideError?.(result.error || 'Failed to set scene voice');
          return result;
        }

        toast.success('Scene voice set');
        onSetOverrideSuccess?.(sceneId, voiceId);
        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        toast.error('Failed to set scene voice', {
          description: errorMessage,
        });
        onSetOverrideError?.(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsSettingOverride(false);
      }
    },
    [reelId, setSceneOverrideMutation, onSetOverrideSuccess, onSetOverrideError]
  );

  /**
   * Remove a voice override for a specific scene
   */
  const handleRemoveSceneOverride = useCallback(
    async (sceneId: Id<'reelScenes'>): Promise<{ success: boolean; error?: string }> => {
      setIsRemovingOverride(true);

      try {
        const result = await removeSceneOverrideMutation({ reelId, sceneId });

        if (!result.success) {
          toast.error('Failed to remove scene voice', {
            description: result.error,
          });
          onRemoveOverrideError?.(result.error || 'Failed to remove scene voice');
          return result;
        }

        toast.success('Scene voice override removed');
        onRemoveOverrideSuccess?.(sceneId);
        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        toast.error('Failed to remove scene voice', {
          description: errorMessage,
        });
        onRemoveOverrideError?.(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsRemovingOverride(false);
      }
    },
    [reelId, removeSceneOverrideMutation, onRemoveOverrideSuccess, onRemoveOverrideError]
  );

  /**
   * Get the effective voice for a scene
   * Returns override voice if set, otherwise default voice
   */
  const getEffectiveVoiceForScene = useCallback(
    (sceneId: Id<'reelScenes'>): Id<'clonedVoices'> | null => {
      // Check for scene-specific override first
      const overrideVoiceId = sceneOverridesMap.get(sceneId);
      if (overrideVoiceId) {
        return overrideVoiceId;
      }

      // Fall back to default voice
      return defaultVoiceId;
    },
    [sceneOverridesMap, defaultVoiceId]
  );

  /**
   * Check if a scene has a voice override
   */
  const hasSceneOverride = useCallback(
    (sceneId: Id<'reelScenes'>): boolean => {
      return sceneOverridesMap.has(sceneId);
    },
    [sceneOverridesMap]
  );

  /**
   * Get the override voice ID for a scene (if any)
   */
  const getSceneOverrideVoiceId = useCallback(
    (sceneId: Id<'reelScenes'>): Id<'clonedVoices'> | null => {
      return sceneOverridesMap.get(sceneId) ?? null;
    },
    [sceneOverridesMap]
  );

  // ============================================
  // RETURN API
  // ============================================

  return {
    // Data
    assignment: assignment as IReelVoiceAssignment | null | undefined,
    defaultVoiceId,
    sceneOverrides,
    sceneOverridesMap,
    isLoading,

    // Computed
    hasDefaultVoice,
    hasAnyOverrides,
    isProcessing,

    // Loading states
    isSettingDefault,
    isSettingOverride,
    isRemovingOverride,
    isClearingDefault,

    // Actions
    setDefaultVoice: handleSetDefaultVoice,
    clearDefaultVoice: handleClearDefaultVoice,
    setSceneOverride: handleSetSceneOverride,
    removeSceneOverride: handleRemoveSceneOverride,

    // Helpers
    getEffectiveVoiceForScene,
    hasSceneOverride,
    getSceneOverrideVoiceId,
  };
}
